import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  deleteDoc,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "./firebase";

// ── Stats (height, birthday) ──────────────────────────────────────────────────

export async function getBodyStats(uid) {
  const snap = await getDoc(doc(db, "bodyMetrics", uid));
  return snap.exists() ? snap.data() : {};
}

export async function saveBodyStats(uid, updates) {
  const docRef = doc(db, "bodyMetrics", uid);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    await updateDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
  } else {
    await setDoc(docRef, { ...updates, updatedAt: serverTimestamp() });
  }
}

// ── Measurements log ──────────────────────────────────────────────────────────

export async function getMeasurementsLog(uid) {
  const q = query(
    collection(db, "bodyMetrics", uid, "measurementsLog"),
    orderBy("date", "desc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function addMeasurementsEntry(uid, { date, chest, shoulders, waist, hip }) {
  await addDoc(collection(db, "bodyMetrics", uid, "measurementsLog"), {
    date,
    chest: chest !== "" && chest != null ? Number(chest) : null,
    shoulders: shoulders !== "" && shoulders != null ? Number(shoulders) : null,
    waist: waist !== "" && waist != null ? Number(waist) : null,
    hip: hip !== "" && hip != null ? Number(hip) : null,
    createdAt: serverTimestamp(),
  });
}

export async function deleteMeasurementsEntry(uid, entryId) {
  await deleteDoc(doc(db, "bodyMetrics", uid, "measurementsLog", entryId));
}

// ── Weight log ────────────────────────────────────────────────────────────────

export async function getWeightLog(uid) {
  const snap = await getDocs(collection(db, "bodyMetrics", uid, "weightLog"));
  const entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return entries.sort((a, b) => (a.date > b.date ? 1 : -1));
}

export async function addWeightEntry(uid, { date, weight, unit }) {
  await addDoc(collection(db, "bodyMetrics", uid, "weightLog"), {
    date,
    weight: Number(weight),
    unit,
    createdAt: serverTimestamp(),
  });
}

export async function deleteWeightEntry(uid, entryId) {
  await deleteDoc(doc(db, "bodyMetrics", uid, "weightLog", entryId));
}

// ── Steps log ────────────────────────────────────────────────────────────────

export async function getStepsLog(uid) {
  const snap = await getDocs(collection(db, "bodyMetrics", uid, "stepsLog"));
  const entries = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  return entries.sort((a, b) => (a.date > b.date ? 1 : -1));
}

export async function addStepsEntry(uid, { date, steps }) {
  await addDoc(collection(db, "bodyMetrics", uid, "stepsLog"), {
    date,
    steps: Number(steps),
    createdAt: serverTimestamp(),
  });
}

export async function upsertStepsEntry(uid, { date, steps, source = "manual" }) {
  await setDoc(
    doc(db, "bodyMetrics", uid, "stepsLog", date),
    {
      date,
      steps: Number(steps),
      source,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function deleteStepsEntry(uid, entryId) {
  await deleteDoc(doc(db, "bodyMetrics", uid, "stepsLog", entryId));
}

// ── Progress photos ───────────────────────────────────────────────────────────

export async function getPhotoLog(uid) {
  const q = query(
    collection(db, "bodyMetrics", uid, "photoLog"),
    orderBy("date", "asc")
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function uploadProgressPhoto(uid, date, angle, file) {
  const path = `progressPhotos/${uid}/${date}/${angle}`;
  const storageRef = ref(storage, path);
  await uploadBytes(storageRef, file);
  return getDownloadURL(storageRef);
}

export async function savePhotoEntry(uid, { date, front, side, back }) {
  // Use date as document ID so each date has one entry (upsert)
  const entryRef = doc(db, "bodyMetrics", uid, "photoLog", date);
  const snap = await getDoc(entryRef);
  const existing = snap.exists() ? snap.data() : {};
  await setDoc(entryRef, {
    date,
    front: front ?? existing.front ?? null,
    side: side ?? existing.side ?? null,
    back: back ?? existing.back ?? null,
    updatedAt: serverTimestamp(),
  });
}

export async function deletePhotoEntry(uid, date, angle) {
  const path = `progressPhotos/${uid}/${date}/${angle}`;
  const storageRef = ref(storage, path);
  try {
    await deleteObject(storageRef);
  } catch {
    // file may not exist in storage, continue
  }
  const entryRef = doc(db, "bodyMetrics", uid, "photoLog", date);
  const snap = await getDoc(entryRef);
  if (!snap.exists()) return;
  const data = snap.data();
  const updated = { ...data, [angle]: null, updatedAt: serverTimestamp() };
  // If all angles are null, delete the entry entirely
  if (!updated.front && !updated.side && !updated.back) {
    await deleteDoc(entryRef);
  } else {
    await setDoc(entryRef, updated);
  }
}
